/**
 * SmartText — renders a prose string that may contain $...$ inline math
 * or $$...$$ block math delimiters using KaTeX, with ASCII-to-LaTeX conversion.
 *
 * Usage:
 *   <SmartText>The formula $x^2 + y^2 = r^2$ describes a circle.</SmartText>
 *   <SmartText>Compute $$\int_0^1 x^2\,dx$$</SmartText>
 */

import React from 'react';
import { InlineMath, BlockMath } from 'react-katex';
import 'katex/dist/katex.min.css';

const ASCII_TO_LATEX = [
  // integral(expr, var) → \int expr \, dvar
  { pattern: /integral\(([^,)]+),\s*([^)]+)\)/g, replace: (_, e, v) => `\\int ${e.trim()} \\, d${v.trim()}` },
  // sqrt(expr) → \sqrt{expr}
  { pattern: /sqrt\(([^)]+)\)/g, replace: (_, e) => `\\sqrt{${e}}` },
  // frac(a, b) → \frac{a}{b}
  { pattern: /\bfrac\(([^,)]+),\s*([^)]+)\)/g, replace: (_, a, b) => `\\frac{${a.trim()}}{${b.trim()}}` },
];

function preprocess(text) {
  let s = text;
  ASCII_TO_LATEX.forEach(({ pattern, replace }) => {
    s = s.replace(pattern, replace);
  });
  return s;
}

/**
 * Split text on $$...$$ and $...$ delimiters.
 * Returns array of { type: 'text'|'inline'|'block', content: string }
 */
function tokenize(text) {
  const tokens = [];
  // Use split with capture group to keep delimiters
  const parts = text.split(/(\$\$[^$]+?\$\$|\$[^$\n]+?\$)/);
  for (const part of parts) {
    if (!part) continue;
    if (part.startsWith('$$') && part.endsWith('$$') && part.length > 4) {
      tokens.push({ type: 'block', content: part.slice(2, -2) });
    } else if (part.startsWith('$') && part.endsWith('$') && part.length > 2) {
      tokens.push({ type: 'inline', content: part.slice(1, -1) });
    } else {
      tokens.push({ type: 'text', content: part });
    }
  }
  return tokens;
}

/** Split a plain-text segment on **bold** markers and return mixed node array. */
function renderBoldSegments(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  if (parts.length === 1) return [text];
  return parts.map((p, i) =>
    p.startsWith('**') && p.endsWith('**') && p.length > 4
      ? <strong key={i}>{p.slice(2, -2)}</strong>
      : p
  );
}

export function SmartText({ children, className, style }) {
  if (!children && children !== 0) return null;
  if (typeof children !== 'string') {
    // Non-string children (elements, numbers, etc.) pass through unchanged
    return <>{children}</>;
  }

  const text = preprocess(String(children));
  const hasMath = /\$/.test(text);

  // Fast path: no math delimiters
  if (!hasMath) {
    return <span className={className} style={style}>{renderBoldSegments(text)}</span>;
  }

  const tokens = tokenize(text);
  return (
    <span className={className} style={style}>
      {tokens.map((t, i) => {
        if (t.type === 'block') {
          return (
            <span key={i} style={{ display: 'block' }}>
              <SafeBlockMath math={t.content} />
            </span>
          );
        }
        if (t.type === 'inline') {
          return <SafeInlineMath key={i} math={t.content} />;
        }
        return <span key={i}>{renderBoldSegments(t.content)}</span>;
      })}
    </span>
  );
}

function SafeInlineMath({ math }) {
  try {
    return <InlineMath math={math} />;
  } catch {
    return <code style={{ color: '#DC2626', fontSize: '0.9em' }}>{math}</code>;
  }
}

function SafeBlockMath({ math }) {
  try {
    return <BlockMath math={math} />;
  } catch {
    return <pre style={{ color: '#DC2626', fontSize: '0.9em', overflowX: 'auto' }}>{math}</pre>;
  }
}

export default SmartText;
